import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Grid from '~/components/core/Atomic/Grid/Grid';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextField from '~/components/core/Molecules/Fields/TextField';
import type { ClaimContactInfo, UnlinkContactFromClaimRequest } from '~/server_shared/openapi/data-contracts';

import { reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import { useClaim } from '../ClaimContainer';
import { useCms } from '../hooks/useCms';

interface UnlinkContactDialogProps {
  contact: ClaimContactInfo;
  onClose: () => void;
  onContactUnlinked: () => Promise<void>;
}

const validationSchema = Yup.object().shape({
  reason: Yup.string().trim().required('Required'),
});

const UnlinkContactDialog: React.FC<UnlinkContactDialogProps> = ({ contact, onClose, onContactUnlinked }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();

  return (
    <Formik
      initialValues={{ reason: '' }}
      validationSchema={validationSchema}
      onSubmit={async (values, formikProps) => {
        try {
          const payload: UnlinkContactFromClaimRequest = { reason: values.reason };
          await axios.post(
            `/api/v1/organizations/${userOrganization.id}/claim_contacts/claims/${claim.id}/contacts/${contact.id}/unlink`,
            payload
          );
          await onContactUnlinked();
          onClose();
        } catch (error) {
          reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit, values, setFieldValue }) => (
        <CardDialog
          title="Unlink Contact from Claim"
          isDialog
          open
          onClose={onClose}
          footerActions={
            <DialogFooterActions
              primaryLabel="Unlink from Claim"
              onClickPrimary={handleSubmit}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <p>
                This will unlink <strong>{contact.full_name}</strong> from claim{' '}
                <strong>{claim.claim_id_display}</strong>. The contact record itself will not be deleted.
              </p>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="reason"
                label="Reason"
                value={values.reason}
                onChange={(val) => setFieldValue('reason', val)}
                fullWidth
              />
            </Grid>
          </Grid>
        </CardDialog>
      )}
    </Formik>
  );
};

export default UnlinkContactDialog;
